import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { format } from "date-fns";
import { FileText, CheckCircle2, AlertCircle, MoreVertical, Pencil, History, CalendarClock, AlertTriangle } from "lucide-react";
import { formatInvoiceAmount, generateInvoiceStatusSummary, SHORTFALL_REASONS, getShortfall, getShortfallReasonLabel } from "@/lib/invoiceUtils";
import { DatePickerField } from "./DatePickerField";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EditInvoiceDialog } from "./EditInvoiceDialog";
import { InvoiceCorrectionHistory } from "./InvoiceCorrectionHistory";
import { useIsAdmin } from "@/hooks/useUserRole";

interface InvoiceManagementProps {
  leadId: string;
  currency?: string;
  currentStatus: string;
}

interface Invoice {
  id: string;
  installment_number: number;
  percentage: number;
  amount: number;
  due_date: string | null;
  invoice_number: string | null;
  status: string;
  issued_at: string | null;
  paid_at: string | null;
  payment_reference: string | null;
  correction_count?: number;
  amount_received?: number | null;
  shortfall_reason?: string | null;
  shortfall_notes?: string | null;
}

export const InvoiceManagement = ({ leadId, currency = "ZAR", currentStatus }: InvoiceManagementProps) => {
  const queryClient = useQueryClient();
  const { isAdmin } = useIsAdmin();
  const [showIssueDialog, setShowIssueDialog] = useState(false);
  const [showPaidDialog, setShowPaidDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [issueDate, setIssueDate] = useState<Date>(new Date());
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentDate, setPaymentDate] = useState<Date>(new Date());
  const [amountReceived, setAmountReceived] = useState<string>("");
  const [shortfallReason, setShortfallReason] = useState<string>("");
  const [shortfallNotes, setShortfallNotes] = useState<string>("");

  // Fetch invoices for this lead
  const { data: invoices, isLoading } = useQuery({
    queryKey: ["invoices", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("*")
        .eq("lead_id", leadId)
        .order("installment_number");

      if (error) throw error;
      return data as Invoice[];
    },
  });

  // Lead travel date — drives the future-booking payment warning
  const { data: leadTravelDate } = useQuery({
    queryKey: ["lead-travel-date", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("start_date")
        .eq("id", leadId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const isFutureTravel = (() => {
    if (!leadTravelDate?.start_date) return false;
    const sixMonthsOut = new Date();
    sixMonthsOut.setMonth(sixMonthsOut.getMonth() + 6);
    return new Date(leadTravelDate.start_date) > sixMonthsOut;
  })();

  const [showDueDialog, setShowDueDialog] = useState(false);
  const [editDueDate, setEditDueDate] = useState<Date | undefined>(undefined);

  const dueDateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedInvoice) return;
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");
      const newDue = editDueDate ? format(editDueDate, "yyyy-MM-dd") : null;
      const { error } = await supabase.from("invoices").update({ due_date: newDue }).eq("id", selectedInvoice.id);
      if (error) throw error;
      await supabase.from("activity_log").insert({
        lead_id: leadId,
        user_id: user.id,
        action: "Invoice Due Date Updated",
        details: `Due date for invoice #${selectedInvoice.invoice_number || selectedInvoice.installment_number}: ${selectedInvoice.due_date || "none"} → ${newDue || "none"}`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices", leadId] });
      queryClient.invalidateQueries({ queryKey: ["activity-log", leadId] });
      queryClient.invalidateQueries({ queryKey: ["finance-cashflow-v2"] });
      toast.success("Due date saved");
      setShowDueDialog(false);
    },
    onError: (error: any) => toast.error(`Failed to save due date: ${error.message}`),
  });

  // Mark invoice as issued
  const issueInvoiceMutation = useMutation({
    mutationFn: async () => {
      if (!selectedInvoice) return;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { error } = await supabase
        .from("invoices")
        .update({
          status: "issued",
          invoice_number: invoiceNumber,
          issued_at: issueDate.toISOString(),
          due_date: dueDate ? format(dueDate, "yyyy-MM-dd") : null,
        })
        .eq("id", selectedInvoice.id);

      if (error) {
        if (error.code === "23505") {
          const { data: owner } = await supabase
            .from("invoices")
            .select("lead_id, leads(client_name)")
            .eq("invoice_number", invoiceNumber)
            .maybeSingle();
          const ownerName = (owner as any)?.leads?.client_name;
          throw new Error(
            `Invoice number ${invoiceNumber} is already in use${ownerName ? ` on the "${ownerName}" lead` : ""}. Release it there first, or choose a different number.`
          );
        }
        throw error;
      }

      // Log activity
      await supabase.from("activity_log").insert({
        lead_id: leadId,
        user_id: user.id,
        action: "Invoice Issued",
        details: `Invoice #${invoiceNumber} for installment ${selectedInvoice.installment_number} (${selectedInvoice.percentage}%) marked as issued`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices", leadId] });
      queryClient.invalidateQueries({ queryKey: ["activity-log", leadId] });
      toast.success("Invoice marked as issued");
      setShowIssueDialog(false);
      setInvoiceNumber("");
    },
    onError: (error: any) => {
      toast.error(`Failed to issue invoice: ${error.message}`);
    },
  });

  // Mark invoice as paid
  const markPaidMutation = useMutation({
    mutationFn: async () => {
      if (!selectedInvoice) return;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const receivedNum = amountReceived === "" ? selectedInvoice.amount : parseFloat(amountReceived);
      const shortfall = getShortfall(selectedInvoice.amount, receivedNum);
      if (shortfall > 0 && !shortfallReason) {
        throw new Error("Please select a reason for the shortfall");
      }
      if (shortfall > 0 && shortfallReason === "other" && !shortfallNotes.trim()) {
        throw new Error("Please add notes explaining the shortfall");
      }

      const { error } = await supabase
        .from("invoices")
        .update({
          status: "paid",
          paid_at: paymentDate.toISOString(),
          payment_reference: paymentReference || null,
          amount_received: receivedNum,
          shortfall_reason: shortfall > 0 ? shortfallReason : null,
          shortfall_notes: shortfall > 0 && shortfallNotes.trim() ? shortfallNotes.trim() : null,
        })
        .eq("id", selectedInvoice.id);

      if (error) throw error;

      // Log activity
      const shortfallDetail = shortfall > 0
        ? `. Received ${formatInvoiceAmount(receivedNum, currency)} (short ${formatInvoiceAmount(shortfall, currency)} — ${getShortfallReasonLabel(shortfallReason)}${shortfallReason === 'other' && shortfallNotes ? `: ${shortfallNotes.trim()}` : ''})`
        : '';
      await supabase.from("activity_log").insert({
        lead_id: leadId,
        user_id: user.id,
        action: "Invoice Paid",
        details: `Invoice #${selectedInvoice.invoice_number} for installment ${selectedInvoice.installment_number} marked as paid${paymentReference ? ` (Ref: ${paymentReference})` : ''}${shortfallDetail}`,
      });

      // Check if all invoices are paid
      const { data: allInvoices } = await supabase
        .from("invoices")
        .select("status")
        .eq("lead_id", leadId);

      const allPaid = allInvoices?.every(inv => 
        inv.status === "paid" || (inv.status === selectedInvoice.status && selectedInvoice.id === selectedInvoice.id)
      );

      if (allPaid) {
        // Update lead status to execution
        await supabase
          .from("leads")
          .update({ status: "executing" })
          .eq("id", leadId);

        await supabase.from("lead_changes").insert({
          lead_id: leadId,
          user_id: user.id,
          field_changed: "status",
          old_value: "invoicing",
          new_value: "executing",
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      queryClient.invalidateQueries({ queryKey: ["activity-log", leadId] });
      toast.success("Invoice marked as paid");
      setShowPaidDialog(false);
      setPaymentReference("");
      setAmountReceived("");
      setShortfallReason("");
      setShortfallNotes("");
    },
    onError: (error: any) => {
      toast.error(`Failed to mark invoice as paid: ${error.message}`);
    },
  });

  const handleIssueInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceNumber("");
    setIssueDate(new Date());
    setDueDate(invoice.due_date ? new Date(invoice.due_date) : undefined);
    setShowIssueDialog(true);
  };

  const handleMarkPaid = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setPaymentReference("");
    setPaymentDate(new Date());
    setAmountReceived(invoice.amount.toString());
    setShortfallReason("");
    setShortfallNotes("");
    setShowPaidDialog(true);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "outline"> = {
      pending: "outline",
      issued: "secondary",
      paid: "default",
    };

    const icons = {
      pending: <AlertCircle className="h-3 w-3 mr-1" />,
      issued: <FileText className="h-3 w-3 mr-1" />,
      paid: <CheckCircle2 className="h-3 w-3 mr-1" />,
    };

    return (
      <Badge variant={variants[status] || "outline"} className="flex items-center">
        {icons[status as keyof typeof icons]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const canIssue = (invoice: Invoice, index: number) => {
    if (invoice.status !== "pending") return false;
    // Can only issue if all previous invoices are issued or paid
    if (index === 0) return true;
    return invoices?.slice(0, index).every(inv => inv.status !== "pending");
  };

  const canMarkPaid = (invoice: Invoice) => {
    return invoice.status === "issued";
  };

  if (isLoading) {
    return <div>Loading invoices...</div>;
  }

  if (!invoices || invoices.length === 0) {
    return null;
  }

  const statusSummary = generateInvoiceStatusSummary(invoices);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Invoice Management</span>
          <Badge variant="secondary" className="text-sm">
            {statusSummary}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Installment</TableHead>
              <TableHead>Percentage</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead>Invoice #</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice, index) => (
              <TableRow key={invoice.id}>
                <TableCell>#{invoice.installment_number}</TableCell>
                <TableCell>{invoice.percentage}%</TableCell>
                <TableCell className="font-medium">
                  {formatInvoiceAmount(invoice.amount, currency)}
                  {invoice.status === 'paid' && invoice.amount_received != null && getShortfall(invoice.amount, invoice.amount_received) > 0 && (
                    <div className="text-xs text-amber-700 mt-1">
                      Received {formatInvoiceAmount(invoice.amount_received, currency)} · Short {formatInvoiceAmount(getShortfall(invoice.amount, invoice.amount_received), currency)}
                      <div className="text-muted-foreground">
                        {getShortfallReasonLabel(invoice.shortfall_reason)}
                        {invoice.shortfall_reason === 'other' && invoice.shortfall_notes ? `: ${invoice.shortfall_notes}` : ''}
                      </div>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {invoice.due_date ? format(new Date(invoice.due_date), "MMM dd, yyyy") : "-"}
                </TableCell>
                <TableCell>{invoice.invoice_number || "-"}</TableCell>
                <TableCell>
                  {getStatusBadge(invoice.status)}
                  {(invoice.correction_count || 0) > 0 && (
                    <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300">
                      Corrected
                    </Badge>
                  )}
                  {invoice.status === 'paid' && invoice.amount_received != null && getShortfall(invoice.amount, invoice.amount_received) > 0 && (
                    <Badge variant="outline" className="ml-2 text-amber-700 border-amber-400">
                      Shortfall
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2 items-center">
                    {canIssue(invoice, index) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleIssueInvoice(invoice)}
                      >
                        Mark as Issued
                      </Button>
                    )}
                    {canMarkPaid(invoice) && (
                      <Button
                        size="sm"
                        onClick={() => handleMarkPaid(invoice)}
                      >
                        Mark as Paid
                      </Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {invoice.status !== "paid" && (
                          <DropdownMenuItem onClick={() => {
                            setSelectedInvoice(invoice);
                            setEditDueDate(invoice.due_date ? new Date(invoice.due_date + "T12:00:00") : undefined);
                            setShowDueDialog(true);
                          }}>
                            <CalendarClock className="h-4 w-4 mr-2" />
                            {invoice.due_date ? "Change Due Date" : "Set Due Date"}
                          </DropdownMenuItem>
                        )}
                        {isAdmin && (
                          <DropdownMenuItem onClick={() => {
                            setSelectedInvoice(invoice);
                            setShowEditDialog(true);
                          }}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Correct Invoice
                          </DropdownMenuItem>
                        )}
                        {(invoice.correction_count || 0) > 0 && (
                          <DropdownMenuItem onClick={() => {
                            setSelectedInvoice(invoice);
                            setShowHistoryDialog(true);
                          }}>
                            <History className="h-4 w-4 mr-2" />
                            View Corrections
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Issue Invoice Dialog */}
        <Dialog open={showIssueDialog} onOpenChange={setShowIssueDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Issue Invoice</DialogTitle>
              <DialogDescription>
                Mark installment #{selectedInvoice?.installment_number} as issued
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="invoice-number">Invoice Number *</Label>
                <div className="flex gap-2">
                  <Input
                    id="invoice-number"
                    placeholder="INV-0325"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={async () => {
                      const { data, error } = await supabase.rpc("generate_invoice_number");
                      if (error) {
                        toast.error(`Could not generate number: ${error.message}`);
                        return;
                      }
                      if (data) setInvoiceNumber(data as string);
                    }}
                  >
                    Suggest next
                  </Button>
                </div>
              </div>
              <DatePickerField
                label="Issue Date"
                value={issueDate}
                onChange={(date) => date && setIssueDate(date)}
                required
              />
              <DatePickerField
                label="Due Date"
                value={dueDate}
                onChange={(date) => setDueDate(date ?? undefined)}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowIssueDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => issueInvoiceMutation.mutate()}
                disabled={!invoiceNumber || issueInvoiceMutation.isPending}
              >
                {issueInvoiceMutation.isPending ? "Issuing..." : "Confirm Issue"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Set Due Date Dialog */}
        <Dialog open={showDueDialog} onOpenChange={setShowDueDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Set Due Date</DialogTitle>
              <DialogDescription>
                Invoice #{selectedInvoice?.invoice_number || selectedInvoice?.installment_number}. The due date drives overdue tracking and cash flow.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <DatePickerField
                label="Due Date"
                value={editDueDate}
                onChange={(date) => setEditDueDate(date ?? undefined)}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDueDialog(false)}>Cancel</Button>
              <Button onClick={() => dueDateMutation.mutate()} disabled={dueDateMutation.isPending}>
                {dueDateMutation.isPending ? "Saving..." : "Save Due Date"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark Paid Dialog */}
        <Dialog open={showPaidDialog} onOpenChange={setShowPaidDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Mark Invoice as Paid</DialogTitle>
              <DialogDescription>
                Confirm payment for invoice #{selectedInvoice?.invoice_number}
              </DialogDescription>
            </DialogHeader>
            {isFutureTravel && leadTravelDate?.start_date && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  Notice: travel date is {format(new Date(leadTravelDate.start_date), "d MMM yyyy")} — more than 6 months away. Confirm this payment is intended for a future booking.
                </span>
              </div>
            )}
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="payment-reference">Payment Reference (Optional)</Label>
                <Input
                  id="payment-reference"
                  placeholder="Payment confirmation number"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                />
              </div>
              <DatePickerField
                label="Payment Date"
                value={paymentDate}
                onChange={(date) => date && setPaymentDate(date)}
                required
              />
              <div className="space-y-2">
                <Label htmlFor="amount-received">Amount Received ({currency})</Label>
                <Input
                  id="amount-received"
                  type="number"
                  step="0.01"
                  value={amountReceived}
                  onChange={(e) => setAmountReceived(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Invoiced: {selectedInvoice ? formatInvoiceAmount(selectedInvoice.amount, currency) : ''}
                </p>
              </div>
              {selectedInvoice && (() => {
                const recv = amountReceived === '' ? selectedInvoice.amount : parseFloat(amountReceived);
                const sf = isNaN(recv) ? 0 : getShortfall(selectedInvoice.amount, recv);
                if (sf <= 0) return null;
                const pct = (sf / selectedInvoice.amount) * 100;
                return (
                  <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-3">
                    <p className="text-sm text-amber-800">
                      Shortfall: {formatInvoiceAmount(sf, currency)} ({pct.toFixed(1)}%)
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="shortfall-reason">Reason for shortfall *</Label>
                      <Select value={shortfallReason} onValueChange={setShortfallReason}>
                        <SelectTrigger id="shortfall-reason">
                          <SelectValue placeholder="Select reason" />
                        </SelectTrigger>
                        <SelectContent>
                          {SHORTFALL_REASONS.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {shortfallReason === 'other' && (
                      <div className="space-y-2">
                        <Label htmlFor="shortfall-notes">Notes *</Label>
                        <Textarea
                          id="shortfall-notes"
                          value={shortfallNotes}
                          onChange={(e) => setShortfallNotes(e.target.value)}
                          placeholder="Explain the shortfall"
                        />
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPaidDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => markPaidMutation.mutate()}
                disabled={markPaidMutation.isPending || (() => {
                  if (!selectedInvoice) return false;
                  const recv = amountReceived === '' ? selectedInvoice.amount : parseFloat(amountReceived);
                  if (isNaN(recv) || recv <= 0) return true;
                  const sf = getShortfall(selectedInvoice.amount, recv);
                  if (sf > 0 && !shortfallReason) return true;
                  if (sf > 0 && shortfallReason === 'other' && !shortfallNotes.trim()) return true;
                  return false;
                })()}
              >
                {markPaidMutation.isPending ? "Processing..." : "Confirm Payment"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Invoice Dialog (Admin Only) */}
        <EditInvoiceDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          invoice={selectedInvoice}
          leadId={leadId}
          currency={currency}
        />

        {/* Correction History Dialog */}
        <InvoiceCorrectionHistory
          open={showHistoryDialog}
          onOpenChange={setShowHistoryDialog}
          invoiceId={selectedInvoice?.id || ""}
          invoiceNumber={selectedInvoice?.invoice_number || selectedInvoice?.installment_number || ""}
          currency={currency}
        />
      </CardContent>
    </Card>
  );
};
